import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import type { ReportLayoutConfig } from '../models/layoutConfig.types'
import type { ScheduleTab, ScheduleTabValue } from '../models/schedule.types'
import { resolveLayout, type LayoutAvailabilityQuery } from '../service/resolveLayout'

import { useReportSchedules } from './useReportSchedules'

import { useWorkspace } from '@/features/account'

function isTabDetail(value: ScheduleTabValue): value is ScheduleTab {
  return typeof value === 'object' && value !== null
}

export interface UseReportLayout {
  /** The layout filtered down to what the selected workspace's schedules can serve. */
  tabs: ComputedRef<ReportLayoutConfig>
}

/**
 * The store-coupled adapter over the pure `resolveLayout` filter (v3 `setReportLayout`).
 * Reactivity flows from the schedule store + workspace attributes through the computed.
 */
export function useReportLayout(
  config: MaybeRefOrGetter<ReportLayoutConfig | null | undefined>,
): UseReportLayout {
  const schedules = useReportSchedules()
  const workspace = useWorkspace()

  function isDataAvailable(query: LayoutAvailabilityQuery): boolean {
    if (query.reportId === undefined) return false
    const selection = schedules.checkScheduleTabs(query.reportId, query.tabs)
    if (!selection) return false
    const details = (Array.isArray(selection) ? selection : [selection]).filter(isTabDetail)

    if (query.checkCols?.length) {
      const cols = query.checkCols
      if (!details.some((tab) => tab.columns?.some((column) => cols.includes(column)))) {
        return false
      }
    }

    if (query.checkDimension) {
      const schedule = schedules.getScheduleByReportId(query.reportId)
      if (!schedule) return false
      const dimension = schedules.dropdownableDimensions(schedule.id, query.checkDimension.field)
      if (!dimension) return false
      // Slim bootstrap metadata may omit `values`; indeterminate fails OPEN so a served tab is
      // never hidden by payload slimming — an actually-empty dimension just renders empty data.
      if (dimension.values !== undefined) {
        const wanted = query.checkDimension.value
        if (!dimension.values.some((value) => wanted.includes(value))) return false
      }
    }

    return true
  }

  const tabs = computed<ReportLayoutConfig>(() => {
    const layout = toValue(config)
    if (!layout) return []
    return resolveLayout(layout, {
      isDataAvailable,
      hasKeyEventMapping: Boolean(workspace.getAttribute('ga4_key_event_mapping')?.value),
    })
  })

  return { tabs }
}
